// src/pages/DashboardReportsPage/DashboardReportsPage.jsx
import React, { useState, useEffect } from "react";
import ReportWidget from "components/dashboard/ReportWidget";
import {
  fetchTotalReservasNoMes,
  fetchUpcomingPayments,
  fetchTotalExcursoesNoMes,
  fetchExcursoesConfirmadas,
} from "services/dashboardService";
import { CalendarDays, Bell, Bus } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "components/ui/card";
import ExcursionsChart from "components/dashboard/ExcursionsChart";

const initialWidgetState = {
  value: 0,
  loading: true,
  error: null,
};

const initialPaymentsState = {
  data: [],
  loading: true,
  error: null,
};

const initialListState = {
  data: [],
  loading: true,
  error: null,
};

const DashboardReportsPage = () => {
  const [reservasData, setReservasData] = useState(initialWidgetState);
  const [paymentsData, setPaymentsData] = useState(initialPaymentsState);
  const [excursoesData, setExcursoesData] = useState(initialWidgetState);
  const [proximasExcursoes, setProximasExcursoes] = useState(initialListState);

  useEffect(() => {
    const loadWidgetData = async (fetcher, setter) => {
      try {
        const result = await fetcher();
        setter({ value: result, loading: false, error: null });
      } catch (err) {
        console.error("Erro ao buscar dados para o widget:", err);
        setter({ value: 0, loading: false, error: err.message });
      }
    };

    const loadPayments = async () => {
      try {
        const result = await fetchUpcomingPayments();
        setPaymentsData({ data: result, loading: false, error: null });
      } catch (err) {
        console.error("Erro ao buscar pagamentos pendentes:", err);
        setPaymentsData({ data: [], loading: false, error: err.message });
      }
    };

    const loadListData = async (fetcher, setter) => {
      try {
        const result = await fetcher();
        setter({ data: result, loading: false, error: null });
      } catch (err) {
        console.error("Erro ao buscar dados da lista:", err);
        setter({ data: [], loading: false, error: err.message });
      }
    };

    loadWidgetData(fetchTotalReservasNoMes, setReservasData);
    loadWidgetData(fetchTotalExcursoesNoMes, setExcursoesData);
    loadPayments();
    loadListData(fetchExcursoesConfirmadas, setProximasExcursoes);
  }, []);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">
          Uma visão geral e em tempo real.
        </p>
      </div>

      <div className="text-center grid gap-6 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4">
        <ReportWidget
          title="Chalés Ocupados no Mês"
          value={reservasData.value}
          loading={reservasData.loading}
          error={reservasData.error}
          icon={CalendarDays}
        />
        <ReportWidget
          title="Excursões no Mês"
          value={excursoesData.value}
          loading={excursoesData.loading}
          error={excursoesData.error}
          icon={Bus}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Pagamentos Pendentes
            </CardTitle>
            <CardDescription>
              Atrasados (vermelho), vencendo hoje (amarelo) e futuros (verde).
            </CardDescription>
          </CardHeader>
          <CardContent>
            {paymentsData.loading ? (
              <p className="text-sm text-muted-foreground">Carregando...</p>
            ) : paymentsData.error ? (
              <p className="text-sm text-destructive">
                Erro ao carregar dados.
              </p>
            ) : paymentsData.data.length > 0 ? (
              <ul className="flex flex-col gap-4">
                {paymentsData.data.map((payment) => {
                  const hoje = new Date();
                  hoje.setHours(0, 0, 0, 0);

                  const dueDate = new Date(payment.depositDueDate);
                  dueDate.setHours(0, 0, 0, 0);

                  let statusClasses = "";
                  if (dueDate < hoje) {
                    // Vencido / Atrasado
                    statusClasses =
                      "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
                  } else if (dueDate.getTime() === hoje.getTime()) {
                    // Vence Hoje
                    statusClasses =
                      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
                  } else {
                    // Vence no Futuro (Em dia)
                    statusClasses =
                      "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
                  }

                  return (
                    <li
                      key={payment.id}
                      className="flex flex-col border-b pb-2 last:border-b-0"
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">
                          {payment.guestInfo}
                        </span>
                        <span
                          className={`text-sm font-bold px-2 py-1 rounded-full ${statusClasses}`}
                        >
                          {payment.depositDueDate.toLocaleDateString("pt-BR")}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        <span>{payment.phoneInfo || "Sem telefone"}</span> |{" "}
                        <span>Chalé: {payment.chaletNumber}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                Nenhum pagamento pendente encontrado.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bus className="h-5 w-5" />
              Próximas Excursões Confirmadas
            </CardTitle>
            <CardDescription>
              As próximas datas com excursões confirmadas.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {proximasExcursoes.loading ? (
              <p className="text-sm text-muted-foreground">Carregando...</p>
            ) : proximasExcursoes.error ? (
              <p className="text-sm text-destructive">
                Erro ao carregar dados.
              </p>
            ) : proximasExcursoes.data.length > 0 ? (
              <ul className="flex flex-col gap-4">
                {proximasExcursoes.data.map((item) => (
                  <li
                    key={item.date.toISOString()}
                    className="flex justify-between items-center border-b pb-2 last:border-b-0"
                  >
                    <span className="font-semibold text-lg">
                      {item.date.toLocaleDateString("pt-BR", {
                        day: "2-digit",
                        month: "long",
                      })}
                    </span>
                    <span className="text-base">
                      <span className="font-bold text-2xl">{item.count}</span>
                      {item.count > 1 ? " excursões" : " excursão"}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                Nenhuma excursão confirmada agendada.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="col-span-1">
          <ExcursionsChart />
        </Card>
      </div>
    </div>
  );
};

export default DashboardReportsPage;
